/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useAiEventStreamStore } from "../provider/AiEventStreamProvider";
import { AiEventStreamEvent, AiEventStreamSnapshot } from "../types/AiEventStream";

export function useAiEventStreamSnapshot(): AiEventStreamSnapshot {
  const store = useAiEventStreamStore();

  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

export function useAiEventStreamControls() {
  const store = useAiEventStreamStore();

  return useMemo(
    () => ({
      connect: store.connect,
      close: store.close,
      reset: store.reset,
      getCurrentStreamId: store.getCurrentStreamId,
      completePromptGeneration: store.completePromptGeneration,
    }),
    [store]
  );
}

export function useAiPromptGenerationStream() {
  const snapshot = useAiEventStreamSnapshot();

  const promptGeneration = snapshot.promptGeneration ?? {
    text: "",
    deltas: [],
    lastDelta: null,
    eventCount: 0,
    isComplete: false,
    model: null,
    contentType: "markdown",
    completionSource: null,
  };

  return {
    text: promptGeneration.text ?? "",
    deltas: promptGeneration.deltas ?? [],
    lastDelta: promptGeneration.lastDelta ?? null,
    eventCount: promptGeneration.eventCount ?? 0,
    isComplete: promptGeneration.isComplete ?? false,
    model: promptGeneration.model ?? null,
    contentType: promptGeneration.contentType ?? "markdown",
    completionSource: promptGeneration.completionSource ?? null,
    status: snapshot.status,
    error: snapshot.error,
    info: snapshot.info,
    streamId: snapshot.streamId,
  };
}

export function useAiEventStreamLifecycle() {
  const snapshot = useAiEventStreamSnapshot();
  const hasVisibleText = (snapshot.promptGeneration.text ?? "").length > 0;
  const hasStreamStarted = snapshot.streamId !== null && snapshot.status !== "idle" && snapshot.status !== "connecting";
  const hasStreamEnded = snapshot.status === "completed" || snapshot.status === "error";

  return {
    hasStreamStarted,
    hasStreamEnded,
    isWaitingForFirstChunk: hasStreamStarted && !hasStreamEnded && !hasVisibleText,
    isStreamingText: hasVisibleText && !hasStreamEnded,
  };
}

export function useAiGraphNodeStream() {
  const snapshot = useAiEventStreamSnapshot();

  return {
    ...snapshot.graphNode,
    status: snapshot.status,
    streamId: snapshot.streamId,
  };
}

export function useAiEventStreamEventHandler<TEvent extends AiEventStreamEvent = AiEventStreamEvent>(
  eventType: string,
  handler: (event: TEvent) => void | Promise<void>
): void {
  const store = useAiEventStreamStore();

  useEffect(() => {
    return store.addEventListener(eventType, handler as (event: AiEventStreamEvent) => void | Promise<void>);
  }, [eventType, handler, store]);
}
